import { Address } from './address';

// ============================================================================
// SUBSCRIPTION & LIMITS (Multi-App Ecosystem)
// ============================================================================

export interface SubscriptionLimits {
  maxProperties: number;              // Free: 1, Basic: 10, Premium: 9999
  maxSharedUsers: number;             // Free: 0, Basic: 3, Premium: 100
  maxSharedPropertiesPerUser: number; // Per share relationship
  allowedRoles: string[];             // Which apps user can access
}

export interface SubscriptionPlan {
  tier: 'free' | 'basic' | 'premium';
  status: 'active' | 'suspended' | 'cancelled';
  currentPeriodStart?: Date;
  currentPeriodEnd?: Date;
  limits: SubscriptionLimits;
}

export interface UserUsage {
  propertyCount: number;
  sharedUserCount: number;
  lastUpdated: Date;
}

// ============================================================================
// ONBOARDING TOUR STATE (New Tour System)
// ============================================================================

export interface OnboardingTourState {
  // Workspace-aware tour completion
  completedTours: {
    [workspaceType: string]: { // 'personal', 'professional', 'service_provider', 'tenant'
      tourIds: string[];
      lastCompletedAt?: Date;
    }
  };

  // Skip tracking
  skippedTours: string[];
  skipCount: number;

  // Welcome popup specific
  welcomePopup: {
    shown: boolean;
    dismissedCount: number;
    lastDismissedAt?: Date;
    neverShowAgain: boolean;
  };

  // Auto-trigger preferences
  autoTriggerEnabled: boolean; // User can disable auto-triggers globally

  // Version control for future updates
  schemaVersion: string; // '1.0.0'
  lastMigrationDate?: Date;
}

// ============================================================================
// USER ONBOARDING (personal, once-per-human)
// ============================================================================

/**
 * High-level "who is this human" segmentation captured during the
 * personal onboarding step. Drives sales segmentation, plan suggestion,
 * and the workspace-onboarding starting point.
 */
export type UserProfessionalContext =
  | 'private_owner'
  | 'multi_owner'
  | 'pro_manager'
  | 'investor'
  | 'syndic_manager'
  | 'service_provider'
  | 'tenant';

/** Stated portfolio size — bucketed for privacy + clean engagement axis. */
export type UserPropertyCountStated =
  | 'none'
  | '1'
  | '2_5'
  | '6_20'
  | '20_plus';

/** Acquisition channel — optional but valuable for marketing attribution. */
export type UserAcquisitionSource =
  | 'search_engine'
  | 'social_media'
  | 'word_of_mouth'
  | 'professional_recommendation'
  | 'advertising'
  | 'other';

/**
 * Fast-read summary of the user's accepted legal documents, written by the
 * `recordUserConsent` callable alongside the immutable `users/{uid}/consents`
 * audit log. Each entry stores the version it relates to so a later version
 * bump is detectably stale (drives the blocking re-consent flow).
 */
export interface UserConsentEntry {
  version: string;
  acceptedAt?: Date | string;
}

export interface UserConsents {
  terms?: UserConsentEntry;
  privacy?: UserConsentEntry;
  /** The data-controller responsibility acknowledgement (versioned as terms). */
  dataResponsibility?: UserConsentEntry;
}

/**
 * Per-class opt-outs for the lifecycle/engagement emails. Written by the
 * `setMyLifecycleEmailPrefs` callable (preference centre) and the one-click
 * unsubscribe endpoint; read live by the daily lifecycle sweep.
 */
export interface LifecycleEmailPrefs {
  unsubscribedAll?: boolean;
  unsubscribedAt?: Date | string;
  channels?: Partial<Record<'activation' | 'reengagement' | 'winback' | 'digest', boolean>>;
}

/**
 * Lifecycle block stored at `users/{uid}.profile`. All mutations flow
 * through callables (`updateUserProfile`, `completeUserOnboarding`,
 * `skipUserOnboarding`, `setMyLifecycleEmailPrefs`); clients cannot write
 * the field directly.
 */
export interface UserProfile {
  firstName: string;
  lastName: string;
  phone?: string;
  professionalContext: UserProfessionalContext;
  propertyCountStated: UserPropertyCountStated;
  acquisitionSource?: UserAcquisitionSource;
  /** "This number is reachable on WhatsApp" (#282). False without a phone. */
  whatsappOptIn?: boolean;
  /**
   * Set when the person opened the confirmation link we sent to that number
   * (#341). Its presence is what tells the e-mail banner that the WhatsApp
   * route is already spent — and, since #427, that the account is open.
   */
  whatsappVerifiedAt?: Date | string;
  marketingConsent: boolean;
  marketingConsentAt?: Date | string;
  /** Accepted legal documents (terms / privacy / data-responsibility). */
  consents?: UserConsents;
  /** Engagement-email opt-outs (communication preference centre). */
  lifecycleEmailPrefs?: LifecycleEmailPrefs;
  /**
   * Whether this person's personal phone/email may be shown to a
   * counterparty who has no other way to reach them (#495). Absent means
   * `true` — deploying this field changes nothing until someone opts out.
   * Set only through `setMyContactVisibility`.
   */
  contactVisible?: boolean;
  locale?: 'en' | 'fr' | 'nl' | 'de';
  startedAt?: Date | string;
  updatedAt?: Date | string;
  completedAt?: Date | string;
  skipped?: boolean;
  skippedAt?: Date | string;
  schemaVersion: 1;
}

// ============================================================================
// USER CLASS
// ============================================================================

export class User {
  userUID: string;
  email: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  mobileNumber?: string;
  photoURL?: string;
  address?: Address;
  contractorUID?: string;
  roles?: ('admin' | 'user' | 'manager' | 'owner' | 'tenant' | 'syndic' | 'contractor' | 'superadmin')[];
  isActive?: boolean;
  betaTester?: boolean;
  createdAt: Date;
  updatedAt: Date;
  lastLogin?: Date;
  preferences?: {
    theme?: 'light' | 'dark' | 'system';
    notifications?: {
      email?: boolean;
      push?: boolean;
      sms?: boolean;
    };
    language?: string;
    dateFormat?: 'belgian' | 'us' | 'iso' | 'european';
    currency?: string;
    currencyFormat?: string;
    timezone?: string;
    // Onboarding-related properties
    onboardingCompleted?: boolean;
    onboardingCompletedAt?: string;
    onboardingPreferencesSet?: boolean;
  };
  
  // Direct onboarding status - eliminates need for complex calculations (LEGACY - keep for backward compatibility)
  onboardingStatus?: 'pending' | 'completed' | 'skipped';

  // New onboarding tour system (workspace-aware, feature-specific tracking)
  onboardingTours?: OnboardingTourState;

  // Personal user-level onboarding answers (once-per-human; written by
  // the `updateUserProfile` / `completeUserOnboarding` callables).
  profile?: UserProfile;

  // Multi-App Ecosystem: Subscription & Usage Tracking
  subscriptionPlan?: SubscriptionPlan;
  currentUsage?: UserUsage;

  // Notification preferences (detailed settings from notifications page)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  notificationPreferences?: Record<string, any>;

  constructor(data: Partial<User> = {}) {
    this.userUID = data.userUID || '';
    this.email = data.email || '';
    this.displayName = data.displayName;
    this.firstName = data.firstName;
    this.lastName = data.lastName;
    this.phoneNumber = data.phoneNumber;
    this.mobileNumber = data.mobileNumber;
    this.photoURL = data.photoURL;
    this.roles = data.roles || [];
    this.isActive = data.isActive;
    this.betaTester = data.betaTester;
    this.contractorUID = data.contractorUID;
    this.address = data.address ? new Address(data.address) : undefined;
    this.createdAt = data.createdAt ? new Date(data.createdAt) : new Date();
    this.updatedAt = data.updatedAt ? new Date(data.updatedAt) : new Date();
    this.lastLogin = data.lastLogin ? new Date(data.lastLogin) : undefined;
    this.preferences = data.preferences;
    this.onboardingStatus = data.onboardingStatus;
    this.onboardingTours = data.onboardingTours;
    this.profile = data.profile;
    this.notificationPreferences = data.notificationPreferences;

    // Initialize subscription plan with defaults (free tier)
    // TODO: TESTING ONLY - Restore to real limits before production!
    this.subscriptionPlan = data.subscriptionPlan || {
      tier: 'free',
      status: 'active',
      limits: {
        maxProperties: 9999,        // Testing: was 1
        maxSharedUsers: 100,         // Testing: was 0
        maxSharedPropertiesPerUser: 100,  // Testing: was 0
        allowedRoles: ['owner']
      }
    };

    // Initialize usage tracking
    this.currentUsage = data.currentUsage || {
      propertyCount: 0,
      sharedUserCount: 0,
      lastUpdated: new Date()
    };

    // Handle Firestore Timestamp conversions for subscription.
    // Values arrive as Firestore `Timestamp` on fresh reads; the inner
    // cast narrows to the `.toDate()` interface without pulling the
    // SDK type into this domain file.
    if (this.subscriptionPlan?.currentPeriodStart && typeof this.subscriptionPlan.currentPeriodStart === 'object' && 'toDate' in this.subscriptionPlan.currentPeriodStart) {
      this.subscriptionPlan.currentPeriodStart = (this.subscriptionPlan.currentPeriodStart as { toDate: () => Date }).toDate();
    }
    if (this.subscriptionPlan?.currentPeriodEnd && typeof this.subscriptionPlan.currentPeriodEnd === 'object' && 'toDate' in this.subscriptionPlan.currentPeriodEnd) {
      this.subscriptionPlan.currentPeriodEnd = (this.subscriptionPlan.currentPeriodEnd as { toDate: () => Date }).toDate();
    }
    if (this.currentUsage?.lastUpdated && typeof this.currentUsage.lastUpdated === 'object' && 'toDate' in this.currentUsage.lastUpdated) {
      this.currentUsage.lastUpdated = (this.currentUsage.lastUpdated as { toDate: () => Date }).toDate();
    }
  }

  hasRole(role: string): boolean {
    // `includes` is typed against the array element union; the caller
    // passes a runtime string, so we widen the check with a cast to
    // the declared element type.
    type RoleName = NonNullable<User['roles']>[number];
    return this.roles ? this.roles.includes(role as RoleName) : false;
  }

  /**
   * Check if user can perform an operation based on subscription limits
   */
  canPerformAction(action: 'add_property' | 'share_property' | 'add_shared_user'): boolean {
    if (!this.subscriptionPlan || !this.currentUsage) return false;

    switch (action) {
      case 'add_property':
        return this.currentUsage.propertyCount < this.subscriptionPlan.limits.maxProperties;
      case 'share_property':
      case 'add_shared_user':
        return this.currentUsage.sharedUserCount < this.subscriptionPlan.limits.maxSharedUsers;
      default:
        return false;
    }
  }

  /**
   * Get remaining limit for a specific action
   */
  getRemainingLimit(type: 'properties' | 'shared_users'): number {
    if (!this.subscriptionPlan || !this.currentUsage) return 0;

    switch (type) {
      case 'properties':
        return Math.max(0, this.subscriptionPlan.limits.maxProperties - this.currentUsage.propertyCount);
      case 'shared_users':
        return Math.max(0, this.subscriptionPlan.limits.maxSharedUsers - this.currentUsage.sharedUserCount);
      default:
        return 0;
    }
  }
}